import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPageUrl } from '../../utils';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Save,
  User,
  MapPin,
  Briefcase,
  Users,
  FileText,
  Upload,
  Trash2,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import IdentityTab from '../../components/debtor/form/IdentityTab';
import AddressTab from '../../components/debtor/form/AddressTab';
import FinancialTab from '../../components/debtor/form/FinancialTab';
import FamilyTab from '../../components/debtor/form/FamilyTab';
import SLIKTab from '../../components/debtor/form/SLIKTab';
import BusinessDocumentsTab from '../../components/debtor/form/BusinessDocumentsTab';
import DocumentsTab from '../../components/debtor/form/DocumentsTab';
import { useDebtorForm } from '../../components/debtor/form/hooks/useDebtorForm';
import { useToast } from '../../context/ToastContext';

const TABS = [
  { id: 'identity', label: 'Identitas', icon: User },
  { id: 'address', label: 'Alamat', icon: MapPin },
  { id: 'financial', label: 'Keuangan', icon: Briefcase },
  { id: 'family', label: 'Keluarga & Relasi', icon: Users },
  { id: 'slik', label: 'SLIK', icon: FileText },
  { id: 'business', label: 'Dokumen Usaha', icon: Briefcase },
];

export default function DebtorForm() {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const urlParams = new URLSearchParams(window.location.search);
  const debtorId = urlParams.get('id');
  const isEdit = !!debtorId;
  const [activeTab, setActiveTab] = useState('identity');

  const {
    loading,
    saving,
    formData,
    handleChange,
    handleFileUpload,
    addRelation,
    updateRelation,
    removeRelation,
    addBusinessDocument,
    updateBusinessDocument,
    removeBusinessDocument,
    saveDebtor,
  } = useDebtorForm(debtorId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await saveDebtor();
      showSuccess(isEdit ? 'Data debitur berhasil diperbarui' : 'Data debitur berhasil dibuat');
      navigate(createPageUrl('DebtorList'));
    } catch (error: any) {
      console.error('Error saving debtor:', error);
      showError(error?.message || 'Gagal menyimpan data debitur');
    }
  };

  const handleBusinessDocFileUpload = async (file: File, index: number) => {
    try {
      const fileUrl = await handleFileUpload(file, 'temp');
      updateBusinessDocument(index, 'file_url', fileUrl);
    } catch (error) {
      showError('Gagal mengupload file');
    }
  };

  const handleRelationFileUpload = async (file: File, index: number) => {
    try {
      const fileUrl = await handleFileUpload(file, 'temp');
      updateRelation(index, 'photo_url', fileUrl);
    } catch (error) {
      showError('Gagal mengupload file');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEdit ? 'Edit Debitur' : 'Tambah Debitur Baru'}
          </h1>
          <p className="text-gray-500 mt-1">
            {isEdit ? 'Perbarui data debitur' : 'Isi data lengkap debitur baru'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          {/* Tab Navigation */}
          <div className="inline-flex h-10 items-center justify-center rounded-md bg-gray-100 p-1">
            {TABS.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
                    activeTab === tab.id
                      ? 'border-b-2 border-orange-500 text-orange-500'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Icon className="w-4 h-4 mr-2" /> {tab.label}
                </button>
              );
            })}
            {isEdit && (
              <button
                type="button"
                onClick={() => setActiveTab('documents')}
                className={`inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
                  activeTab === 'documents'
                    ? 'border-b-2 border-orange-500 text-orange-500'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <FileText className="w-4 h-4 mr-2" /> Dokumen
              </button>
            )}
          </div>

          {/* Tab Content with Smooth Animation - Only content animates */}
          <div className="relative">
            <AnimatePresence mode="wait">
              {activeTab === 'identity' && (
                <motion.div
                  key="identity"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                >
                  <Card>
                    <CardHeader>
                      <CardTitle>Data Identitas</CardTitle>
                      <CardDescription>Informasi identitas dasar debitur</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <IdentityTab
                        formData={formData}
                        handleChange={handleChange}
                        handleFileUpload={handleFileUpload}
                        errors={{}}
                      />
                    </CardContent>
                  </Card>
                </motion.div>
              )}

              {activeTab === 'address' && (
                <motion.div
                  key="address"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                  className="space-y-6"
                >
                  <Card>
                    <CardHeader>
                      <CardTitle>Data Alamat</CardTitle>
                      <CardDescription>Alamat lengkap debitur</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <AddressTab formData={formData} handleChange={handleChange} />
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <MapPin className="w-5 h-5 text-amber-600" />
                        Lokasi pada Peta
                      </CardTitle>
                      <CardDescription>Masukkan koordinat lokasi alamat debitur</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label>Latitude</Label>
                          <Input
                            type="number"
                            step="any"
                            value={formData.latitude || ''}
                            onChange={e =>
                              handleChange(
                                'latitude',
                                e.target.value ? parseFloat(e.target.value) : null
                              )
                            }
                            placeholder="-6.2088"
                          />
                        </div>
                        <div>
                          <Label>Longitude</Label>
                          <Input
                            type="number"
                            step="any"
                            value={formData.longitude || ''}
                            onChange={e =>
                              handleChange(
                                'longitude',
                                e.target.value ? parseFloat(e.target.value) : null
                              )
                            }
                            placeholder="106.8456"
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              )}

              {activeTab === 'financial' && (
                <motion.div
                  key="financial"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                >
                  <Card>
                    <CardHeader>
                      <CardTitle>Data Keuangan</CardTitle>
                      <CardDescription>
                        Informasi pekerjaan, penghasilan, dan riwayat bank
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <FinancialTab formData={formData} handleChange={handleChange} />
                    </CardContent>
                  </Card>
                </motion.div>
              )}

              {activeTab === 'family' && (
                <motion.div
                  key="family"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                  className="space-y-6"
                >
                  <Card>
                    <CardHeader>
                      <CardTitle>Data Keluarga</CardTitle>
                      <CardDescription>Informasi pasangan dan kontak darurat</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <FamilyTab formData={formData} handleChange={handleChange} />
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader>
                      <CardTitle>Relasi Debitur</CardTitle>
                      <CardDescription>Keluarga atau relasi bisnis yang terkait</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {formData.relations.map((relation: any, index: number) => (
                          <Card key={index}>
                            <CardContent className="p-4 space-y-3">
                              <div className="flex justify-between items-start">
                                <h4 className="font-medium">Relasi #{index + 1}</h4>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="text-red-500"
                                  onClick={() => removeRelation(index)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                              <div className="grid md:grid-cols-2 gap-3">
                                <Input
                                  placeholder="Nama"
                                  value={relation.name}
                                  onChange={e => updateRelation(index, 'name', e.target.value)}
                                />
                                <Input
                                  placeholder="Tempat & Tanggal Lahir"
                                  value={relation.birth_info}
                                  onChange={e =>
                                    updateRelation(index, 'birth_info', e.target.value)
                                  }
                                />
                                <Input
                                  placeholder="Relasi dengan Debitur"
                                  value={relation.relation}
                                  onChange={e => updateRelation(index, 'relation', e.target.value)}
                                />
                                <Input
                                  type="file"
                                  accept="image/*"
                                  onChange={e => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                      handleRelationFileUpload(file, index);
                                    }
                                  }}
                                />
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                        <Button
                          type="button"
                          onClick={addRelation}
                          variant="outline"
                          className="w-full"
                        >
                          <Upload className="w-4 h-4 mr-2" /> Tambah Relasi
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              )}

              {activeTab === 'slik' && (
                <motion.div
                  key="slik"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                >
                  <Card>
                    <CardHeader>
                      <CardTitle>Data SLIK</CardTitle>
                      <CardDescription>Informasi hasil pengecekan SLIK OJK</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <SLIKTab formData={formData} handleChange={handleChange} />
                    </CardContent>
                  </Card>
                </motion.div>
              )}

              {activeTab === 'business' && (
                <motion.div
                  key="business"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                >
                  <Card>
                    <CardHeader>
                      <CardTitle>Dokumen Usaha / Kerja</CardTitle>
                      <CardDescription>
                        Upload dokumen pendukung usaha atau pekerjaan
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <BusinessDocumentsTab
                        businessDocuments={formData.business_documents}
                        onAdd={addBusinessDocument}
                        onUpdate={updateBusinessDocument}
                        onRemove={removeBusinessDocument}
                        onFileUpload={handleBusinessDocFileUpload}
                      />
                    </CardContent>
                  </Card>
                </motion.div>
              )}

              {isEdit && activeTab === 'documents' && (
                <motion.div
                  key="documents"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                >
                  <Card>
                    <CardHeader>
                      <CardTitle>Dokumen Debitur</CardTitle>
                      <CardDescription>
                        Upload dan kelola dokumen wajib serta dokumen pendukung
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <DocumentsTab debtorId={debtorId!} />
                    </CardContent>
                  </Card>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Submit Button - Fixed outside animation */}
        <div className="flex justify-end gap-3 mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(-1)}
            className="bg-white border border-gray-300 text-gray-900 font-semibold hover:bg-gray-50"
          >
            Batal
          </Button>
          <Button
            type="submit"
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold"
            disabled={saving}
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Menyimpan...' : 'Simpan'}
          </Button>
        </div>
      </form>
    </div>
  );
}
